import React, { useState, useEffect, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import map from 'lodash/map';
import { customTheme } from 'theme';

import { ButtonGroup } from 'components/DataEntry';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { BarChart, LineChart } from 'components/Visualization/Charts';
import { lineChartStylesVulnerabilityDashboard } from 'containers/Visibility/Vulnerabilities/Components/styles';
import { vulnerabilityChartItems } from 'containers/Visibility/Vulnerabilities/Components/utils';
import { VulnerabilityChartProps } from 'containers/Visibility/Vulnerabilities/types';
import { formatDate } from 'utils/date';

export const HostsWithUnpatchedOSAndTechnology = (
  props: VulnerabilityChartProps,
) => {
  const {
    severities,
    showResolved = false,
    cumulative = false,
    chartType,
    updateValue,
  } = props;
  const [data, setData] = useState<any>([]);

  useEffect(() => {
    const osData = severities.data?.unpatched_os;
    const technologyData = severities.data?.unpatched_technology;
    const totals: Record<string, number> = {};
    const items = map(osData, (o, date) => {
      const values: Record<string, number> = {
        'Unpatched OS': o,
        'Patched OS': o,
        'Unpatched Technology': technologyData?.[date],
        'Patched Technology': technologyData?.[date],
      };
      if (cumulative) {
        Object.entries(values).forEach(([key, value]) => {
          totals[key] = (totals[key] ?? 0) + Number(value ?? 0);
          values[key] = totals[key];
        });
      }
      return { Date: formatDate(date, 'D-MMM'), ...values };
    });

    setData(items);
  }, [severities.data, cumulative]);

  const commonFields = useMemo(
    () => ({
      xField: 'Date',
      yField: showResolved
        ? ['Patched OS', 'Patched Technology']
        : ['Unpatched OS', 'Unpatched Technology'],
      data: data,
      isLoading: severities.isLoading,
      colorMap: {
        'Unpatched OS': customTheme.colors.medium,
        'Patched OS': customTheme.colors.medium,
        'Unpatched Technology': customTheme.colors.low,
        'Patched Technology': customTheme.colors.low,
      },
      styles: lineChartStylesVulnerabilityDashboard,
    }),
    [data, severities.isLoading, showResolved],
  );

  return (
    <DashboardWidget
      label={
        showResolved
          ? `Hosts with Resolved Vulnerabilities - By Type`
          : 'Hosts to Patch - By Type'
      }
      subHeader="Last 7 Days"
      rightHeader={
        <Box>
          <ButtonGroup
            items={vulnerabilityChartItems}
            value={chartType}
            onClick={updateValue}
            styles={{ button: { size: 'xs' } }}
          />
        </Box>
      }
      content={
        <Box w="full" h="full">
          {chartType?.value === 'line' ? (
            <LineChart {...commonFields} />
          ) : (
            <BarChart {...commonFields} stacked />
          )}
        </Box>
      }
      styles={{
        contentWrapper: { px: 0, pt: 0, pb: 1 },
        rightHeader: { hStack: { w: 'full', h: 'full', align: 'start' } },
      }}
    />
  );
};
